import { debounce, simpleSearchMultiKey } from '@/helpers/utils'
import { useCallback, useMemo, useState } from 'react'

interface UseSimpleMultiKeySearch<TItem> {
  delay?: number
  items: TItem[]
  keyMinLength?: number
  keys: (keyof TItem)[]
}

export type UseSimpleMultiKeySearchReturn = ReturnType<
  typeof useSimpleMultiKeySearch
>

/**
 * A hook to perform a simple search on a list of items based on a key.
 * @param delay The delay in milliseconds to wait before updating the search results.
 * @param keyMinLength The minimum length of the search key before performing the search.
 */
export const useSimpleMultiKeySearch = <T extends Record<string, unknown>>({
  delay = 150,
  items,
  keyMinLength = 2,
  keys,
}: UseSimpleMultiKeySearch<T>) => {
  const [searchTerm, setSearchTerm] = useState<string>('')

  const handleSetSearchTerm = useCallback(({ search }: { search: string }) => {
    const trimmedSearch = search.trim()
    setSearchTerm(trimmedSearch)
  }, [])

  const handleSearchReset = useCallback(() => {
    setSearchTerm('')
  }, [])

  const debouncedSetSearchTerm = debounce(handleSetSearchTerm, delay)

  const memoizedSearchResults = useMemo(() => {
    // return early if there are no items
    if (!items?.length) {
      return []
    }
    // sync search results with the search term or items when they change
    if (searchTerm && searchTerm.length >= keyMinLength) {
      const results = simpleSearchMultiKey({
        items: items,
        keys: keys,
        search: searchTerm,
      })
      return results
    } else {
      // Reset the search results if the search key is too short
      return items
    }
  }, [items, keys, keyMinLength, searchTerm])

  return {
    handleSearch: debouncedSetSearchTerm,
    handleSearchReset,
    searchResults: memoizedSearchResults,
    searchTerm,
  }
}
